import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';
import { useUpdateTotalNotification } from '../../utils/useUpdateTotalNotification';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import moment from 'moment';

// COMPONENTS
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';

// TYPES
import { FeedbackResolveCommand } from '../../api/models';

interface ResolveModalTypes {
	feedbackOccurrence?: string;
	feedbackId?: string;
}

const ResolveModal: React.FC<ResolveModalTypes> = ({ feedbackOccurrence, feedbackId }) => {
	const history = useHistory();

	// Get all Feedback Resolution from Redux store
	const feedbackResolutions = useAppSelector((state) => state.lists.feedbackResolutions);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	// Set up data state and initial data
	const initialFeedbackResolveFormData: FeedbackResolveCommand = {
		id: feedbackId,
		occurrence: feedbackOccurrence,
		resolution: undefined,
		notes: ''
	};

	const [feedbackResolveFormData, setFeedbackResolveFormData] = React.useState(initialFeedbackResolveFormData);

	const displayActions = (value: string | undefined) => {
		if (value !== undefined) {
			if (value === 'InsufficientInformation') {
				return (
					<>
						<strong>Insufficient information — </strong>No further action required
					</>
				);
			} else if (value === 'FurtherEnquiriesMade') {
				return (
					<>
						<strong>Further enquiries made — </strong>No further action required
					</>
				);
			} else if (value === 'ActionTaken') {
				return (
					<>
						<strong>Action taken — </strong>Please describe below
					</>
				);
			} else if (value === 'EscalatedToIncident') {
				return (
					<>
						<strong>Escalated to an incident report</strong>
					</>
				);
			} else if (value === 'EscalatedToComplaint') {
				return (
					<>
						<strong>Escalated to a complaint</strong>
					</>
				);
			} else if (value === 'Other') {
				return (
					<>
						<strong>Other... — </strong>Please describe below
					</>
				);
			} else {
				return null;
			}
		}
	};

	const resolveFeedback = async (feedbackResolveFormData: FeedbackResolveCommand) => {
		if (feedbackResolveFormData !== undefined) {
			setIsLoading(true);

			const response = await apibridge.resolveFeedback(feedbackResolveFormData);

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				setIsLoading(false);

				history.push('/feedback');

				useUpdateTotalNotification();
			}
		}
	};

	const submitHandler = (values: any) => {
		const data: FeedbackResolveCommand = {
			...feedbackResolveFormData,

			id: feedbackId,
			occurrence: moment().format('YYYY-MM-DDTHH:mm:ss'),
			resolution: values.resolutionInput,
			notes: values.notesInput
		};

		setFeedbackResolveFormData(data);

		resolveFeedback(data);
	};

	const validationSchema = Yup.object({
		resolutionInput: Yup.string().required('Required'),
		notesInput: Yup.string().when('resolutionInput', {
			is: (resolutionInput: string) => resolutionInput === 'ActionTaken' || resolutionInput === 'Other',
			then: Yup.string().required('Required')
		})
	});

	const initialValues = {
		resolutionInput: feedbackResolveFormData.resolution,
		notesInput: feedbackResolveFormData.notes
	};

	return (
		<>
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<Formik
								initialValues={initialValues}
								validationSchema={validationSchema}
								onSubmit={submitHandler}
								enableReinitialize
							>
								{(formik) => {
									return (
										<Form translate="yes" className="general-form">
											<div className="form-group">
												<label className="label mbtom-14">State type of resolution</label>
												<div className="form-field">
													{feedbackResolutions &&
														feedbackResolutions.length > 0 &&
														feedbackResolutions.map((item, index) => {
															return (
																<div key={index} className="radio-row mbtom-20">
																	<Field
																		name="resolutionInput"
																		type="radio"
																		value={item.value}
																		checked={formik.values.resolutionInput === item.value}
																		onChange={() => formik.setFieldValue('resolutionInput', item.value)}
																	/>

																	<label htmlFor="resolutionInput" className="radio-option">
																		{displayActions(item.value)}
																	</label>
																</div>
															);
														})}

													<ErrorMessage name="typeInput" component="div" className="error-message" />
												</div>
											</div>

											<div className="form-group">
												<label htmlFor="notesInput" className="label">
													Notes
												</label>

												<div className="form-field">
													<Field as="textarea" name="notesInput" id="notesInput" type="text" className="form-input" />

													<ErrorMessage name="notesInput" component="div" className="error-message" />
												</div>
											</div>

											<div className="form-button-group">
												<Button type="submit" className="btn-primary w-100">
													Submit
												</Button>
											</div>
										</Form>
									);
								}}
							</Formik>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</>
	);
};

export default ResolveModal;
